import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { Icon } from "../../kit";
import type { DomainRead } from "../../../state/editor/types";
import { notice, useBindingCtx, useCollapsed, useDomains, useEditorStore, useUi } from "../../../state/editor/useEditor";
import { basicEngineFor } from "../basic/applyEngine";
import { SECTION_HELP } from "../sections/help";
import { recordedBatch, recordedWrite } from "./history";
import { CompactCheck, EditScope, InertChip, Marked, RecordOff } from "./rows";
import { RowRenderer } from "./RowRenderer";
import { sectionDomains, type Binding, type SectionDef, type TabId } from "./schema";
import { SectionCtx } from "./sectionContext";
import {
  captureValues,
  copiedSection,
  copySection,
  deletePreset,
  presetRows,
  presetsFor,
  savePreset,
  subscribePresets,
  writesFor,
} from "./sectionPresets";

/**
 * One collapsible inspector section. Subscribes to the union of domains its
 * rows touch (re-rendering the section — ~≤12 rows — when any changes), then
 * renders each row through the single RowRenderer. Collapsed state lives in the
 * persisted `ui` slice keyed `${tab}.${section}`.
 */
export function InspectorSection({
  tabId,
  section,
  forceOpen,
  onCollapseOthers,
  mark,
}: {
  tabId: TabId;
  section: SectionDef;
  forceOpen?: boolean;
  /** Search terms to mark in titles, labels and the description. */
  mark?: string[];
  /** Supplied by the panel, which is the only thing that knows the siblings.
   *  It is what keeps the kebab from ever being a dead affordance. */
  onCollapseOthers?: () => void;
}) {
  const store = useEditorStore();
  const ctx = useBindingCtx();
  const domains = useMemo(() => sectionDomains(section), [section]);
  useDomains(domains);
  const read = useMemo<DomainRead>(() => (d) => store.getState(d), [store]);
  const [collapsedMap, setCollapsed] = useCollapsed();
  const [ui, patchUi] = useUi();

  // "See in Advanced" (Basic mode) flashes the rows a control just moved.
  const flash = ui.highlight;
  const flashed = useMemo(() => {
    const prefix = `${tabId}.${section.id}.`;
    return new Set((flash?.rows ?? []).filter((p) => p.startsWith(prefix)).map((p) => p.slice(prefix.length)));
  }, [flash, tabId, section.id]);
  const firstFlashId = section.rows.find((r) => flashed.has(r.id))?.id ?? null;
  // Scroll when the flashed row MOUNTS, not when the jump happens: material
  // rows render only once the part selection has loaded its material, so at
  // jump time there is nothing to scroll to yet (measured — the panel stayed
  // at the top of the tab). Once per highlight.
  const scrolledFor = useRef<number | null>(null);
  const onFlashMount = useCallback(
    (el: HTMLDivElement | null) => {
      if (!el || !flash || scrolledFor.current === flash.at) return;
      scrolledFor.current = flash.at;
      // Instant, not smooth: a smooth scroll never runs in a hidden tab
      // (measured: document.hidden → scrollTop unchanged), and a jump the
      // user asked for should land at once anyway.
      el.scrollIntoView({ block: "center" });
      // Sections above can still grow after this row mounts (the Node section
      // fills in once the selection's details load), pushing it back down.
      // Re-centre once that has settled.
      window.setTimeout(() => {
        if (el.isConnected) el.scrollIntoView({ block: "center" });
      }, 450);
    },
    [flash],
  );
  useEffect(() => {
    if (!flash || flashed.size === 0) return;
    const t = window.setTimeout(() => patchUi({ highlight: null }), 3200);
    return () => window.clearTimeout(t);
  }, [flash, flashed, patchUi]);

  const [infoOpen, setInfoOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [naming, setNaming] = useState<string | null>(null);
  const presets = useSyncExternalStore(subscribePresets, () => presetsFor(section.id));

  const key = `${tabId}.${section.id}`;
  const title = section.displayTitle ?? section.title;
  const pins = useMemo(() => new Set(ui.pins), [ui.pins]);
  const togglePin = useCallback(
    (path: string) => patchUi({ pins: pins.has(path) ? ui.pins.filter((p) => p !== path) : [...ui.pins, path] }),
    [pins, ui.pins, patchUi],
  );
  const sectionInfo = useMemo(
    () => ({ tabId, sectionId: section.id, title, mark, pins, togglePin, showExperimental: ui.showExperimental }),
    [tabId, section.id, title, mark, pins, togglePin, ui.showExperimental],
  );
  const editScope = useMemo(
    () => ({
      section: title,
      record: (e: { label: string; key?: string; undo: () => Promise<void>; redo: () => Promise<void> }) =>
        basicEngineFor(store).recordExternal(e),
    }),
    [title, store],
  );

  // Experimental / debug / duplicate sections stay out of the way until asked for.
  if (section.experimental && !ui.showExperimental && !forceOpen) return null;

  const collapsed = forceOpen ? false : (collapsedMap[key] ?? section.defaultCollapsed ?? false);

  // A persisted-open tab can render before its eager seed lands; nullable
  // domains (selection/material) and the always-present ui slice are exempt.
  const pending = domains.some(
    (d) => d !== "ui" && d !== "selection" && d !== "material" && store.getState(d) === undefined,
  );
  if (!pending && section.visible && !section.visible(read, ctx)) return null;

  // The effect's on/off lives in the header, as a switch — the old coloured dot
  // said whether it was on but could not turn it on. The section's own
  // "Enabled" row is the same value, so it is not drawn twice.
  const onBinding = section.enabledBadge ?? null;
  const on = !pending && onBinding ? onBinding.get(read, ctx) : null;
  const enabledRowId = onBinding
    ? section.rows.find((r) => r.kind === "checkbox" && /^enabled?( shadow)?$/i.test(r.label.trim()))?.id ?? null
    : null;
  const headerFlash = enabledRowId !== null && flashed.has(enabledRowId);

  const help = SECTION_HELP[section.id] ?? null;
  // Search: rows whose NAME does not show the words matched on their help or
  // keywords. Said once for the section — a line under every such row doubled
  // the height of a result group.
  // When the section's own title or description matched, every row is
  // shown by that, and a per-row explanation would be wrong.
  const sectionHit = !!mark?.some((t) => t.length > 1 && `${title} ${section.title} ${help ?? ""}`.toLowerCase().includes(t));
  const quietHits = mark?.length && !sectionHit
    ? section.rows
        .filter((r) => r.label && r.kind !== "custom" && !mark.some((t) => t.length > 1 && r.label.toLowerCase().includes(t)))
        .map((r) => r.label.replace(/\s*\([^)]*\)\s*$/, ""))
    : [];
  const sectionPinned = pins.has(key);
  const values = () => captureValues(section, read, ctx);
  const canPreset = !pending && presetRows(section, read, ctx).length > 0;
  const apply = (vals: Record<string, unknown>, label: string) => {
    const n = recordedBatch(store, ctx, writesFor(section, vals, read, ctx), label);
    if (n === 0) notice(`${title}: already set that way.`);
  };

  const menu: { label: string; run: () => void; disabled?: boolean; aside?: () => void }[] = [
    { label: sectionPinned ? "Unpin section" : "Pin section", run: () => togglePin(key) },
    ...(canPreset
      ? [
          { label: "Copy settings", run: () => copySection(section.id, values()) },
          {
            label: "Paste settings",
            disabled: copiedSection?.sectionId !== section.id,
            run: () => { if (copiedSection?.sectionId === section.id) apply(copiedSection.values, `${title} · paste`); },
          },
          { label: "Save as preset…", run: () => { setNaming(""); setMenuOpen(true); } },
          ...presets.map((p) => ({
            label: `Apply “${p.name}”`,
            run: () => apply(p.values, `${title} · preset “${p.name}”`),
            aside: () => deletePreset(section.id, p.name),
          })),
        ]
      : []),
    ...(onCollapseOthers ? [{ label: "Collapse other sections", run: onCollapseOthers }] : []),
    ...(section.menu ?? []).map((m) => ({ label: m.label, run: () => void m.run(ctx) })),
  ];

  const renderRow = (row: SectionDef["rows"][number]) => {
    if (row.id === enabledRowId) return null;
    return flashed.has(row.id) ? (
      <div
        key={row.id}
        ref={row.id === firstFlashId ? onFlashMount : undefined}
        className="mx-1 rounded-md bg-ed-active ring-2 ring-ed-accent/50 transition-colors"
        data-flash-row={row.id}
      >
        <RowRenderer row={row} read={read} ctx={ctx} />
      </div>
    ) : (
      <RowRenderer key={row.id} row={row} read={read} ctx={ctx} />
    );
  };

  return (
    <div className="border-b border-ed-border" data-section={key}>
      {/* ⚠️ A flex ROW, not one big button. The ⓘ, the switch and the ⋯ are
          siblings of the collapse toggle: nesting them inside it would be
          invalid HTML and their clicks would also toggle the section. */}
      <div
        data-section-header
        ref={headerFlash && enabledRowId === firstFlashId ? onFlashMount : undefined}
        className={`flex h-10 items-center gap-1 pr-2 hover:bg-ed-hover ${headerFlash ? "bg-ed-active ring-2 ring-inset ring-ed-accent/50" : ""}`}
        data-flash-row={headerFlash ? enabledRowId ?? undefined : undefined}
      >
        <button
          type="button"
          aria-expanded={!collapsed}
          onClick={() => setCollapsed(key, !collapsed)}
          className="flex h-full min-w-0 flex-1 items-center gap-1.5 pl-2.5 pr-1 text-left"
        >
          <Icon name={collapsed ? "chevron_right" : "expand_more"} size={18} className="shrink-0 text-ed-text-3" />
          <span className={`min-w-0 flex-1 truncate text-[13px] font-semibold ${on === false ? "text-ed-text-2" : "text-ed-text"}`}>
            <Marked text={title} terms={mark} />
          </span>
          {section.experimental ? (
            <span title={section.experimental} className="shrink-0 rounded border border-ed-line px-1 text-[11px] leading-[16px] text-ed-text-2">
              Experimental
            </span>
          ) : null}
          {sectionPinned ? <Icon name="star" size={14} fill className="shrink-0 text-ed-accent" /> : null}
          {section.inert ? <InertChip tag={section.inert} /> : null}
        </button>
        {section.info ? (
          <button
            type="button"
            title="More about this"
            aria-label={`More about ${title}`}
            aria-expanded={infoOpen}
            onClick={() => setInfoOpen((v) => !v)}
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded ${
              infoOpen ? "text-ed-accent" : "text-ed-text-3 hover:text-ed-text"
            }`}
          >
            <Icon name="info" size={15} />
          </button>
        ) : null}
        {onBinding && on !== null ? (
          <RecordOff>
            <CompactCheck
              checked={on}
              label={`${title}: ${on ? "on" : "off"}`}
              onChange={(v) => recordedWrite(store, ctx, onBinding as Binding<unknown>, v, `${title} · ${v ? "on" : "off"}`)}
            />
          </RecordOff>
        ) : null}
        <div className="relative shrink-0">
          <button
            type="button"
            title="Section actions"
            aria-label={`${title} actions`}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => { setNaming(null); setMenuOpen((v) => !v); }}
            className="flex h-6 w-6 items-center justify-center rounded text-ed-text-3 hover:bg-ed-panel hover:text-ed-text"
          >
            <Icon name="more_horiz" size={16} />
          </button>
          {menuOpen ? (
            <>
              <div className="fixed inset-0 z-40" onClick={() => { setMenuOpen(false); setNaming(null); }} />
              <div role="menu" className="absolute right-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-lg border border-ed-border bg-ed-panel py-1 shadow-[0_6px_20px_rgba(0,0,0,0.12)]">
                {naming !== null ? (
                  <form
                    className="flex flex-col gap-2 p-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const name = naming.trim();
                      if (!name) return;
                      savePreset(section.id, name, values());
                      setNaming(null);
                      setMenuOpen(false);
                      notice(`Saved “${name}” — find it in this section's ⋯ menu.`);
                    }}
                  >
                    <label className="text-[12px] font-semibold text-ed-text-2" htmlFor={`${key}-preset`}>
                      Preset name
                    </label>
                    <input
                      id={`${key}-preset`}
                      data-own-focus
                      autoFocus
                      value={naming}
                      maxLength={40}
                      placeholder={`My ${title.toLowerCase()}`}
                      onChange={(e) => setNaming(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Escape") { setNaming(null); setMenuOpen(false); } }}
                      className="rounded-md border border-ed-line px-2 py-1 text-[13px] text-ed-text outline-none focus:border-ed-accent focus:ring-2 focus:ring-ed-accent/20"
                    />
                    <div className="flex justify-end gap-1.5">
                      <button type="button" onClick={() => setNaming(null)} className="rounded-md px-2.5 py-1 text-[12px] text-ed-text-2 hover:bg-ed-hover">
                        Cancel
                      </button>
                      <button type="submit" disabled={!naming.trim()} className="rounded-md bg-ed-text px-2.5 py-1 text-[12px] font-medium text-white disabled:opacity-40">
                        Save
                      </button>
                    </div>
                  </form>
                ) : (
                  menu.map((m) => (
                    <div key={m.label} className="group/item flex items-center hover:bg-ed-hover">
                      <button
                        role="menuitem"
                        type="button"
                        disabled={m.disabled}
                        onClick={() => {
                          if (m.label !== "Save as preset…") setMenuOpen(false);
                          m.run();
                        }}
                        className="min-w-0 flex-1 truncate px-3 py-1.5 text-left text-[13px] text-ed-text disabled:text-ed-text-3"
                      >
                        {m.label}
                      </button>
                      {m.aside ? (
                        <button
                          type="button"
                          title="Delete preset"
                          aria-label={`Delete ${m.label.replace(/^Apply /, "")}`}
                          onClick={() => m.aside?.()}
                          className="mr-1.5 flex h-6 w-6 items-center justify-center rounded text-ed-text-3 opacity-0 hover:text-ed-red focus-visible:opacity-100 group-hover/item:opacity-100"
                        >
                          <Icon name="delete" size={14} />
                        </button>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
      {infoOpen && section.info ? (
        <p className="border-t border-ed-border bg-ed-panel-2 px-3 py-2 text-[12px] leading-snug text-ed-text-2">
          {section.info}
        </p>
      ) : null}
      {!collapsed && !pending ? (
        <SectionCtx.Provider value={sectionInfo}>
          <EditScope.Provider value={editScope}>
            {help ? (
              <p className="-mt-1 px-3 pb-1.5 pl-[34px] text-[12px] leading-[1.4] text-ed-text-3">
                <Marked text={help} terms={mark} />
              </p>
            ) : null}
            {quietHits.length ? (
              <p className="px-3 pb-1.5 pl-[34px] text-[11px] leading-[1.4] text-ed-text-3" data-quiet-hits>
                Matched in the description of {quietHits.slice(0, 4).join(", ")}
                {quietHits.length > 4 ? ` and ${quietHits.length - 4} more` : ""}.
              </p>
            ) : null}
            <div className={`pb-2 transition-opacity ${on === false ? "opacity-60" : ""}`} data-effect-off={on === false || undefined}>
              {section.rows.map(renderRow)}
            </div>
          </EditScope.Provider>
        </SectionCtx.Provider>
      ) : null}
    </div>
  );
}
